import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity, ArrowRight, CalendarCheck2, CalendarDays, CalendarPlus, ChevronDown,
  CheckCircle2, Clock3, CreditCard, FileBarChart, Footprints, Gauge, Headphones,
  ListOrdered, UserPlus, UserRound, Users, XCircle,
  RefreshCw,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSubscription } from "../context/SubscriptionContext";
import buildApiUrl from "../services/apiHelpers";

const DASHBOARD_CSS = `
.cq-dashboard{
  --ink:#0a142b;--bg:#f2f4fa;--card:#ffffff;--line:#e3e8f4;--muted:#5c6b84;
  --blue:#1f4fd8;--blue-dark:#0d2159;--blue-soft:#eaf0fe;
  --sky:#2f79e0;--sky-soft:#eaf3ff;
  --slate:#57657a;--slate-soft:#eef1f6;
  --rose:#b0433c;--rose-soft:#faeceb;
  color:var(--ink);font-family:Inter,system-ui,sans-serif;min-height:100%;background:var(--bg);
  padding:32px 32px 56px;max-width:1400px;margin:0 auto;
}
.cq-dashboard *{box-sizing:border-box}
.cq-dashboard h1,.cq-dashboard h2,.cq-dashboard p{margin:0}
.cq-dashboard button{font:inherit;cursor:pointer;border:none;background:none;color:inherit}
.cq-dashboard .cq-num{font-variant-numeric:tabular-nums}
.cq-dashboard .cq-ico{display:inline-flex;flex:none}
.cq-dashboard .cq-error{padding:12px 14px;margin-bottom:18px;border:1px solid #f0c5c0;border-radius:12px;background:#fff2f0;color:#b83b31;font-size:13px}

/* Hero */
.cq-dashboard .cq-hero{
  position:relative;overflow:hidden;border-radius:22px;padding:34px 38px 28px;color:#eef2fb;
  background:radial-gradient(120% 150% at 100% -20%, rgba(255,255,255,.10), transparent 55%),
             linear-gradient(150deg,#070d1f 0%,#0d2159 48%,#1f4fd8 100%);
  box-shadow:0 20px 44px -22px rgba(6,14,40,.55);
}
.cq-dashboard .cq-hero::after{content:"";position:absolute;inset:0;border-radius:22px;box-shadow:inset 0 1px 0 rgba(255,255,255,.14);pointer-events:none}
.cq-dashboard .cq-hero-top{display:flex;align-items:flex-start;justify-content:space-between;gap:24px;flex-wrap:wrap;position:relative}
.cq-dashboard .cq-hero-title{font-weight:700;font-size:clamp(24px,2.4vw,32px);letter-spacing:0;line-height:1.15;color:#fff}
.cq-dashboard .cq-hero-clinic-name{background:linear-gradient(110deg,#fff0b8 0%,#d4af37 52%,#f8dc83 100%);background-clip:text;-webkit-background-clip:text;color:transparent;-webkit-text-fill-color:transparent;filter:drop-shadow(0 1px 8px rgba(212,175,55,.15))}
.cq-dashboard .cq-hero-sub{font-size:13.5px;color:#b9c6ec;margin-top:8px;max-width:48ch;line-height:1.55}
.cq-dashboard .cq-hero-meta{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.cq-dashboard .cq-sync-button{display:inline-flex;align-items:center;justify-content:center;gap:7px;padding:8px 12px;border:1px solid rgba(245,207,114,.48);border-radius:10px;background:linear-gradient(135deg,rgba(212,175,55,.2),rgba(212,175,55,.07));color:#f5cf72;font-size:12px;font-weight:700;transition:background .15s,border-color .15s,opacity .15s}
.cq-dashboard .cq-sync-button:hover:not(:disabled){border-color:rgba(245,207,114,.8);background:linear-gradient(135deg,rgba(212,175,55,.3),rgba(212,175,55,.12))}
.cq-dashboard .cq-sync-button:disabled{cursor:wait;opacity:.72}
.cq-dashboard .cq-sync-icon-spinning{animation:cq-dashboard-spin .8s linear infinite}
@keyframes cq-dashboard-spin{to{transform:rotate(360deg)}}
.cq-dashboard .cq-pill{display:inline-flex;align-items:center;gap:7px;border:1px solid rgba(255,255,255,.16);background:rgba(255,255,255,.06);border-radius:10px;padding:8px 13px;color:#cdd8f2;font-size:12px;font-weight:600;white-space:nowrap}
.cq-dashboard .cq-pill-live{color:#a9c8ff;background:rgba(169,200,255,.14);border-color:rgba(169,200,255,.28)}
.cq-dashboard .cq-dot{width:6px;height:6px;border-radius:50%;background:currentColor;flex:none}
.cq-dashboard .cq-usage-value{color:#fff;font-variant-numeric:tabular-nums}

.cq-dashboard .cq-hero-stats{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:14px;margin-top:26px;position:relative}
.cq-dashboard .cq-stat{border-radius:14px;padding:16px 17px 14px;background:rgba(255,255,255,.055);border:1px solid rgba(255,255,255,.11);min-width:0}
.cq-dashboard .cq-stat-icon{color:#8fb2ff;margin-bottom:13px}
.cq-dashboard .cq-stat-value{font-size:clamp(18px,2vw,22px);font-weight:700;letter-spacing:-.01em;color:#fff;white-space:nowrap}
.cq-dashboard .cq-stat-label{font-size:11.5px;color:#aeb9dd;margin-top:4px}
.cq-dashboard .cq-stat-caption{font-size:10.5px;color:#8390b8;margin-top:10px;padding-top:9px;border-top:1px solid rgba(255,255,255,.09)}
.cq-dashboard .cq-stat-live{background:linear-gradient(135deg,rgba(120,160,255,.16),rgba(255,255,255,.05));border-color:rgba(150,180,255,.3)}
.cq-dashboard .cq-stat-live .cq-stat-icon{color:#dbe6ff}
.cq-dashboard .cq-stat-live .cq-stat-value{color:#fff}

/* Sections / cards */
.cq-dashboard .cq-section{margin-top:22px}
.cq-dashboard .cq-grid{display:grid;gap:18px}
.cq-dashboard .cq-card{background:var(--card);border:1px solid var(--line);border-radius:14px;min-width:0}
.cq-dashboard .cq-list-card{overflow:hidden}
.cq-dashboard .cq-row-2{grid-template-columns:repeat(2,minmax(0,1fr));align-items:start}
.cq-dashboard .cq-two{grid-template-columns:1.6fr 1fr;align-items:start}

.cq-dashboard .cq-head{display:flex;align-items:center;gap:10px;padding:16px 20px;border-bottom:1px solid var(--line)}
.cq-dashboard .cq-head-icon{color:var(--blue)}
.cq-dashboard .cq-head h2{font-size:14px;font-weight:700;letter-spacing:-.005em}
.cq-dashboard .cq-head-right{margin-left:auto;display:flex;align-items:center;gap:14px}
.cq-dashboard .cq-link{display:inline-flex;align-items:center;gap:5px;color:var(--blue);font-size:12.5px;font-weight:700}
.cq-dashboard .cq-link:hover{text-decoration:underline}
.cq-dashboard .cq-live{display:inline-flex;align-items:center;gap:6px;color:var(--blue-dark);font-size:11.5px;font-weight:700}
.cq-dashboard .cq-live span{width:6px;height:6px;border-radius:50%;background:var(--blue)}

/* Tables — lean 3-column layout, no horizontal scroll */
.cq-dashboard .cq-table-scroll{height:360px;overflow-y:auto;overscroll-behavior:contain}
.cq-dashboard .cq-list-table th,.cq-dashboard .cq-list-table td{padding-left:14px;padding-right:14px}
.cq-dashboard .cq-list-table thead{position:sticky;top:0;z-index:1}
.cq-dashboard .cq-list-table th{height:40px}
.cq-dashboard .cq-list-table tbody tr{height:64px}
.cq-dashboard .cq-list-table td{vertical-align:middle}
.cq-dashboard .cq-list-table td:first-child{white-space:nowrap}
.cq-dashboard .cq-list-table td:nth-child(2){overflow:hidden}
.cq-dashboard .cq-list-table td:nth-child(2) > span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cq-dashboard .cq-list-table .cq-sub{display:block}
.cq-dashboard .cq-list-table .cq-empty{height:320px;display:grid;place-items:center}
.cq-dashboard table{width:100%;border-collapse:collapse;table-layout:fixed}
.cq-dashboard th{padding:10px 20px;background:#fafbff;border-bottom:1px solid var(--line);color:var(--muted);font-size:10.5px;text-align:left;font-weight:600}
.cq-dashboard th:last-child,.cq-dashboard td:last-child{text-align:right}
.cq-dashboard td{padding:12px 20px;border-bottom:1px solid #f0f2f8;color:#3a4356;font-size:12.5px;vertical-align:top}
.cq-dashboard tbody tr:last-child td{border-bottom:0}
.cq-dashboard tbody tr:hover td{background:#fafbff}
.cq-dashboard .cq-strong{color:var(--ink);font-weight:700;display:block}
.cq-dashboard .cq-sub{color:var(--muted);font-size:11px;margin-top:2px}
.cq-dashboard .cq-token{display:inline-block;padding:4px 9px;border-radius:7px;background:var(--blue-soft);color:var(--blue-dark);font-weight:700;font-size:11px}
.cq-dashboard .cq-token-walkin{background:var(--sky-soft);color:var(--sky)}
.cq-dashboard .cq-status{display:inline-block;min-width:78px;padding:5px 9px;border-radius:7px;text-align:center;font-size:10px;font-weight:700;white-space:nowrap;background:var(--slate-soft);color:var(--slate)}
.cq-dashboard .cq-status-consulting{background:var(--sky-soft);color:var(--sky)}
.cq-dashboard .cq-status-arrived{background:var(--blue-soft);color:var(--blue-dark)}
.cq-dashboard .cq-status-completed{background:#dfe7ff;color:var(--blue-dark)}
.cq-dashboard .cq-status-cancelled,
.cq-dashboard .cq-status-no-show{background:var(--rose-soft);color:var(--rose)}
.cq-dashboard .cq-empty{padding:34px 20px;text-align:center;color:var(--muted);font-size:12.5px}

/* Doctors — full-width horizontal cards */
.cq-dashboard .cq-doctors{display:grid;grid-template-columns:repeat(auto-fit,minmax(250px,1fr));gap:14px;padding:18px 20px}
.cq-dashboard .cq-doctor-card{border:1px solid var(--line);border-radius:12px;padding:15px 16px;display:flex;flex-direction:column;gap:12px}
.cq-dashboard .cq-doctor-top{display:flex;align-items:center;gap:11px}
.cq-dashboard .cq-avatar{display:grid;place-items:center;width:38px;height:38px;border-radius:10px;background:var(--blue-soft);color:var(--blue-dark);font-size:12px;font-weight:700;flex:none}
.cq-dashboard .cq-doctor-name{font-size:13px;font-weight:700}
.cq-dashboard .cq-doctor-specialty{font-size:11px;color:var(--muted);margin-top:2px}
.cq-dashboard .cq-badge{margin-left:auto;padding:4px 9px;border-radius:999px;background:var(--blue-soft);color:var(--blue-dark);font-size:10px;font-weight:700;white-space:nowrap}
.cq-dashboard .cq-badge-off{background:var(--slate-soft);color:var(--muted)}
.cq-dashboard .cq-doctor-foot{display:flex;align-items:center;gap:6px;color:var(--muted);font-size:11px;padding-top:11px;border-top:1px solid #f0f2f8}

/* Chart + activity */
.cq-dashboard .cq-chart-wrap{padding:20px 22px 18px}
.cq-dashboard .cq-chart-title{font-size:14px;font-weight:700}
.cq-dashboard .cq-chart-sub{font-size:11px;color:var(--muted);margin-top:2px}
.cq-dashboard .cq-chart-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;flex-wrap:wrap}
.cq-dashboard .cq-range{display:inline-flex;align-items:center;gap:6px;border:1px solid var(--line);border-radius:9px;padding:7px 11px;font-size:11.5px;font-weight:600;color:var(--slate)}
.cq-dashboard .cq-line-chart{margin-top:10px;padding-left:28px}
.cq-dashboard .cq-line-chart svg.cq-plot{width:100%;height:200px;overflow:visible;display:block}
.cq-dashboard .cq-line-grid{stroke:#eef1f8;stroke-width:1}
.cq-dashboard .cq-line-axis{fill:#9aa5be;font-size:10px;font-family:Inter}
.cq-dashboard .cq-line-path{fill:none;stroke-width:2.25;stroke-linecap:round;stroke-linejoin:round}
.cq-dashboard .cq-line-dot{stroke:#fff;stroke-width:2}
.cq-dashboard .cq-line-area{stroke:none;opacity:.10}
.cq-dashboard .cq-line-marker{stroke:var(--blue);stroke-width:1.3;stroke-dasharray:4 4}
.cq-dashboard .cq-line-callout{fill:var(--ink);opacity:.92}
.cq-dashboard .cq-line-callout-text{fill:#fff;font-size:10.5px;font-weight:700;font-family:Inter}
.cq-dashboard .cq-legend{display:flex;gap:18px;flex-wrap:wrap;margin-top:12px;padding-left:28px}
.cq-dashboard .cq-legend span{display:inline-flex;align-items:center;gap:6px;color:var(--muted);font-size:10.5px}
.cq-dashboard .cq-legend i{width:8px;height:8px;border-radius:50%;display:block;flex:none}

.cq-dashboard .cq-activity{display:flex;align-items:flex-start;gap:11px;padding:12px 20px;border-bottom:1px solid #f0f2f8}
.cq-dashboard .cq-activity:last-child{border-bottom:0}
.cq-dashboard .cq-activity-icon{display:grid;place-items:center;width:26px;height:26px;border-radius:8px;background:var(--blue-soft);color:var(--blue-dark);flex:none;margin-top:1px}
.cq-dashboard .cq-activity-text{font-size:12px;color:#3a4356;line-height:1.4}
.cq-dashboard .cq-activity-time{margin-left:auto;color:var(--muted);font-size:10px;flex:none;padding-top:2px;white-space:nowrap}

/* Quick actions / plan / support */
.cq-dashboard .cq-actions{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:10px;padding:18px 20px}
.cq-dashboard .cq-quick{display:flex;flex-direction:column;align-items:flex-start;gap:10px;min-width:0;padding:14px;border:1px solid var(--line);border-radius:12px;background:#fff;text-align:left;transition:border-color .15s,background .15s}
.cq-dashboard .cq-quick:hover{border-color:#b9caf5;background:#fafbff}
.cq-dashboard .cq-quick-icon{display:grid;place-items:center;width:34px;height:34px;border-radius:10px;background:var(--blue-soft);color:var(--blue-dark)}
.cq-dashboard .cq-quick-label{font-size:12px;font-weight:700;color:var(--ink);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%}

.cq-dashboard .cq-plan{display:flex;align-items:center;gap:13px;padding:20px;background:linear-gradient(135deg,#eef2ff,#f5f8ff);border:1px solid #dde5fb;border-radius:14px}
.cq-dashboard .cq-plan-icon{display:grid;place-items:center;width:36px;height:36px;border-radius:10px;background:#fff;color:var(--blue);border:1px solid #dde5fb;flex:none}
.cq-dashboard .cq-plan-title{font-size:13.5px;font-weight:700}
.cq-dashboard .cq-plan-note{font-size:11px;color:var(--muted);margin-top:3px}

.cq-dashboard .cq-support{display:flex;align-items:center;gap:12px;padding:18px 20px;background:#fff;border:1px solid var(--line);border-radius:14px;text-align:left;transition:border-color .15s}
.cq-dashboard .cq-support:hover{border-color:#b9caf5}
.cq-dashboard .cq-support-icon{display:grid;place-items:center;width:36px;height:36px;border-radius:10px;background:var(--blue-soft);color:var(--blue-dark);flex:none}
.cq-dashboard .cq-support-title{display:block;font-size:12.5px;font-weight:700}
.cq-dashboard .cq-support-note{display:block;font-size:11px;color:var(--muted);margin-top:3px;line-height:1.4}
.cq-dashboard .cq-support-arrow{color:#9aa8c4;margin-left:auto;flex:none}

.cq-dashboard .cq-foot{display:flex;justify-content:space-between;gap:12px;margin-top:26px;color:#8a95ac;font-size:11px}

@media(max-width:1240px){
  .cq-dashboard .cq-hero-stats{grid-template-columns:repeat(3,minmax(0,1fr))}
  .cq-dashboard .cq-actions{grid-template-columns:repeat(3,minmax(0,1fr))}
}
@media(max-width:980px){
  .cq-dashboard{padding:22px 20px 36px}
  .cq-dashboard .cq-hero{padding:26px 24px 22px;border-radius:18px}
  .cq-dashboard .cq-hero-stats{grid-template-columns:repeat(2,minmax(0,1fr))}
  .cq-dashboard .cq-row-2,.cq-dashboard .cq-two{grid-template-columns:1fr}
}
@media(max-width:620px){
  .cq-dashboard{padding:16px 14px 28px}
  .cq-dashboard .cq-hero{padding:22px 18px 18px}
  .cq-dashboard .cq-hero-title{font-size:23px}
  .cq-dashboard .cq-hero-stats{grid-template-columns:1fr}
  .cq-dashboard .cq-actions{grid-template-columns:repeat(2,minmax(0,1fr))}
  .cq-dashboard th,.cq-dashboard td{padding:10px 12px}
  .cq-dashboard .cq-foot{flex-direction:column;gap:4px}
}
`;

const formatTime = (value) => {
  if (!value) return "-";
  const date = new Date(`1970-01-01T${String(value).slice(0, 8)}`);
  return Number.isNaN(date.getTime()) ? String(value).slice(0, 5) : date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
};
const formatDate = (value) => value ? new Date(`${value}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
const getTodayIST = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
const getTimeOfDayGreeting = () => {
  const hour = Number(new Intl.DateTimeFormat("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", hourCycle: "h23" }).format(new Date()));
  if (hour >= 5 && hour < 12) return "Good morning";
  if (hour < 17 && hour >= 12) return "Good afternoon";
  if (hour < 21 && hour >= 17) return "Good evening";
  return "Hello";
};
const getAppointmentDate = (appointment) => String(appointment?.appointment_date || appointment?.appointmentDate || appointment?.date || "").slice(0, 10);
const initials = (name) => String(name || "").replace(/^Dr\.\s*/i, "").split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase() || "DR";
const statusClass = (status) => `cq-status-${String(status || "").toLowerCase().replaceAll("_", "-")}`;
const statusLabel = (status) => String(status || "-").replaceAll("_", " ").replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());

function StatTile({ icon: Icon, value, label, caption, live }) {
  return (
    <div className={`cq-stat${live ? " cq-stat-live" : ""}`}>
      <span className="cq-ico cq-stat-icon"><Icon size={22} /></span>
      <div className="cq-stat-value cq-num">{value}</div>
      <div className="cq-stat-label">{label}</div>
      <div className="cq-stat-caption">{caption}</div>
    </div>
  );
}

function CardHead({ icon: Icon, title, action, onAction, live }) {
  return (
    <div className="cq-head">
      <span className="cq-ico cq-head-icon"><Icon size={16} /></span>
      <h2>{title}</h2>
      <div className="cq-head-right">
        {live && <span className="cq-live"><span /> Live</span>}
        {action && <button className="cq-link" onClick={onAction}>{action}<ArrowRight size={13} /></button>}
      </div>
    </div>
  );
}

function activityIcon(status) {
  const s = String(status || "").toUpperCase();
  if (s === "COMPLETED") return CheckCircle2;
  if (s === "CANCELLED" || s === "NO_SHOW") return XCircle;
  return CalendarCheck2;
}

function PatientFlowGraph({ rows }) {
  if (!rows.length) return <div className="cq-empty">No weekly appointment data available.</div>;

  const width = 680, chartHeight = 160;
  const maxValue = Math.max(...rows.flatMap((row) => [Number(row.appointments || 0), Number(row.walkins || 0), Number(row.seen || 0)]), 1);
  const point = (index, value) => ({
    x: rows.length === 1 ? width / 2 : (index / (rows.length - 1)) * width,
    y: chartHeight - (Number(value || 0) / maxValue) * chartHeight,
  });
  const pathFor = (key) => rows.map((row, index) => {
    const current = point(index, row[key]);
    return `${index === 0 ? "M" : "L"} ${current.x} ${current.y}`;
  }).join(" ");
  const areaFor = (key) => `${pathFor(key)} L ${width} ${chartHeight} L 0 ${chartHeight} Z`;
  const currentIndex = rows.findIndex((row) => row.current);
  const current = currentIndex >= 0 ? point(currentIndex, rows[currentIndex].appointments) : null;
  const gridValues = [0, 0.25, 0.5, 0.75, 1];
  const colors = { appointments: "#1f4fd8", walkins: "#2f79e0", seen: "#57657a" };

  return (
    <div className="cq-line-chart">
      <svg className="cq-plot" viewBox={`0 0 ${width} 200`} role="img" aria-label="Weekly appointments and completed patients">
        {gridValues.map((ratio) => {
          const y = chartHeight - ratio * chartHeight;
          return (
            <g key={ratio}>
              <line className="cq-line-grid" x1="0" x2={width} y1={y} y2={y} />
              <text className="cq-line-axis" x="-10" y={y + 3} textAnchor="end">{Math.round(maxValue * ratio)}</text>
            </g>
          );
        })}
        {Object.keys(colors).map((key) => <path key={`area-${key}`} className="cq-line-area" d={areaFor(key)} fill={colors[key]} />)}
        {Object.keys(colors).map((key) => <path key={`line-${key}`} className="cq-line-path" d={pathFor(key)} stroke={colors[key]} />)}
        {current && (
          <>
            <line className="cq-line-marker" x1={current.x} x2={current.x} y1="0" y2={chartHeight} />
            <rect className="cq-line-callout" x={Math.min(current.x + 12, width - 102)} y="6" width="92" height="28" rx="8" />
            <text className="cq-line-callout-text" x={Math.min(current.x + 24, width - 90)} y="24">{rows[currentIndex].appointments} patients</text>
            <circle className="cq-line-dot" cx={current.x} cy={current.y} r="5.5" fill={colors.appointments} />
          </>
        )}
        {rows.map((row, index) => {
          const a = point(index, row.appointments), w = point(index, row.walkins), s = point(index, row.seen);
          return (
            <g key={row.hour || row.label || index}>
              <circle className="cq-line-dot" cx={a.x} cy={a.y} r="3" fill={colors.appointments} />
              <circle className="cq-line-dot" cx={w.x} cy={w.y} r="3" fill={colors.walkins} />
              <circle className="cq-line-dot" cx={s.x} cy={s.y} r="3" fill={colors.seen} />
              <text className="cq-line-axis" x={a.x} y={chartHeight + 22} textAnchor="middle">{row.label || row.hour}</text>
            </g>
          );
        })}
      </svg>
      <div className="cq-legend">
        <span><i style={{ background: colors.appointments }} />Appointments</span>
        <span><i style={{ background: colors.walkins }} />Walk-ins</span>
        <span><i style={{ background: colors.seen }} />Patients seen</span>
      </div>
    </div>
  );
}

export default function DashboardHome() {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { subscriptionData, refreshSubscription } = useSubscription();
  const clinicId = auth?.clinic?.id;
  const clinicName = auth?.clinic?.name;
  const [greeting, setGreeting] = useState(getTimeOfDayGreeting);
  const [summary, setSummary] = useState(null);
  const [patientFlow, setPatientFlow] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [error, setError] = useState("");
  const [lastSynced, setLastSynced] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const today = getTodayIST();

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = DASHBOARD_CSS;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setGreeting(getTimeOfDayGreeting()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const loadDashboard = useCallback(async (isCurrent = () => true) => {
    if (!clinicId) return;
    const options = { credentials: "include", headers: { "X-Clinic-Id": String(clinicId) } };
    try {
      const responses = await Promise.all([
        fetch(buildApiUrl(`clinic/dashboard/summary/${clinicId}`), options),
        fetch(buildApiUrl(`clinic/dashboard/patient-flow/${clinicId}`), options),
        fetch(buildApiUrl(`appointments/by-clinic/${clinicId}?startDate=${today}&endDate=${today}`), options),
        fetch(buildApiUrl(`doctors/by-clinic-id/${clinicId}`), options),
        fetch(buildApiUrl(`doctor-booking/active-sessions/${clinicId}`), options),
      ]);
      if (!isCurrent()) return;
      const failed = responses.find((response) => !response.ok);
      if (failed) {
        const payload = await failed.clone().json().catch(() => null);
        throw new Error(payload?.message || `Dashboard data request failed (${failed.status})`);
      }
      const [summaryData, flowData, appointmentsData, doctorsData, sessionsData] = await Promise.all(responses.map((response) => response.json()));
      if (!isCurrent()) return;
      setSummary(summaryData || {});
      setPatientFlow(Array.isArray(flowData) ? flowData : []);
      setAppointments(Array.isArray(appointmentsData) ? appointmentsData : []);
      setDoctors(Array.isArray(doctorsData) ? doctorsData : []);
      setSessions(Array.isArray(sessionsData) ? sessionsData : []);
      setLastSynced(new Date());
      setError("");
    } catch (loadError) {
      if (isCurrent()) setError(loadError.message || "Unable to load dashboard data.");
    }
  }, [clinicId, today]);

  useEffect(() => {
    if (!clinicId) return undefined;
    let cancelled = false;
    const load = () => loadDashboard(() => !cancelled);
    load();
    const timer = window.setInterval(load, 30000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [clinicId, loadDashboard]);

  const handleDashboardSync = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await Promise.all([loadDashboard(), refreshSubscription()]);
    } catch {
      setError("Unable to refresh dashboard data. Please try again.");
    } finally {
      setIsRefreshing(false);
    }
  };

  const data = summary || {};
  const todayAppointments = appointments.filter((appointment) => getAppointmentDate(appointment) === today && !["CANCELLED", "RESCHEDULED", "NO_SHOW"].includes(appointment.status));
  const queue = todayAppointments.filter((appointment) => ["BOOKED", "ARRIVED", "WALK_IN_PENDING", "CONSULTING"].includes(appointment.status)).sort((a, b) => Number(a.token_no || 0) - Number(b.token_no || 0));
  const activeDoctorIds = new Set(sessions.map((session) => String(session.doctorId)));
  const appointmentCounts = appointments.reduce((counts, appointment) => { const key = appointment.doctor_id; counts[key] = (counts[key] || 0) + 1; return counts; }, {});
  const recentActivity = [...todayAppointments].sort((a, b) => String(b.slot_time || "").localeCompare(String(a.slot_time || ""))).slice(0, 5);
  const activeSessions = sessions.length;
  const doctorCount = doctors.length;
  const annualUsage = Number(subscriptionData?.durationMonths) === 12 && subscriptionData?.annualUsageEnabled;
  const usagePeriod = annualUsage ? "Annual" : "Monthly";
  const usedVisits = subscriptionData?.usedAppointments == null ? "—" : Number(subscriptionData.usedAppointments).toLocaleString("en-IN");
  const totalAllowed = subscriptionData?.totalAllowed == null ? "—" : Number(subscriptionData.totalAllowed).toLocaleString("en-IN");
  const remainingVisits = subscriptionData?.remainingAppointments ?? (
    subscriptionData?.totalAllowed != null && subscriptionData?.usedAppointments != null
      ? Math.max(Number(subscriptionData.totalAllowed) - Number(subscriptionData.usedAppointments), 0)
      : null
  );
  const remainingText = remainingVisits == null ? "—" : Number(remainingVisits).toLocaleString("en-IN");
  const quickActions = useMemo(() => [
    ["New patient", UserPlus, "/admin-dashboard/patients"],
    ["Book appointment", CalendarPlus, "/admin-dashboard/appointments"],
    ["Add walk-in", Footprints, "/admin-dashboard/appointments"],
    ["Manage OPD", Gauge, sessions[0]?.doctorId ? `/admin-dashboard/doctor-opd/${sessions[0].doctorId}/manage` : "/admin-dashboard/doctors"],
    ["View reports", FileBarChart, "/admin-dashboard/revenue"],
  ], [sessions]);

  if (!clinicId) {
    return <div className="cq-dashboard"><div className="cq-error">No clinic is selected for this session. Please sign in again through the clinic portal.</div></div>;
  }

  return (
    <div className="cq-dashboard">
      {error && <div className="cq-error" role="alert">{error}</div>}
      {/* ================= HERO ================= */}
      <div className="cq-hero">
        <div className="cq-hero-top">
          <div>
            <h1 className="cq-hero-title">{greeting}{clinicName ? <>, <span className="cq-hero-clinic-name">{clinicName}</span></> : ""}</h1>
            <p className="cq-hero-sub">Here's how your clinic is moving right now — queue, appointments and today's doctors in one place.</p>
          </div>
          <div className="cq-hero-meta">
            <span className="cq-pill">{usagePeriod} used: <strong className="cq-usage-value">{usedVisits} / {totalAllowed}</strong></span>
            <span className="cq-pill cq-pill-live">Remaining: <strong className="cq-usage-value">{remainingText}</strong></span>
            <button className="cq-sync-button" type="button" onClick={handleDashboardSync} disabled={isRefreshing} title="Refresh dashboard data" aria-label="Refresh dashboard data">
              <RefreshCw size={14} className={isRefreshing ? "cq-sync-icon-spinning" : ""} />
              <span>{isRefreshing ? "Syncing" : "Sync"}</span>
            </button>
          </div>
        </div>
        <div className="cq-hero-stats">
          <StatTile icon={CalendarDays} value={data.todayAppointments ?? todayAppointments.length} label="Total appointments" caption="Scheduled for today" />
          <StatTile icon={Users} value={data.completedPatients ?? 0} label="Patients seen" caption="Completed consultations" />
          <StatTile icon={Footprints} value={queue.length} label="Waiting patients" caption="In the live queue now" />
          <StatTile icon={Clock3} value={`${data.averageWaitMinutes ?? 0} min`} label="Average wait" caption="Across today's visits" />
          <StatTile icon={Activity} value={activeSessions ? "Running" : "Not running"} label="OPD status" caption={`${activeSessions} of ${doctorCount} doctors in OPD`} live />
        </div>
      </div>

      {/* ================= QUEUE + APPOINTMENTS (side by side, fixed five-row lists) ================= */}
      <section className="cq-section cq-grid cq-row-2">
        <article className="cq-card cq-list-card">
          <CardHead icon={ListOrdered} title="Live OPD queue" action="View queue" onAction={() => navigate("/admin-dashboard/queue-display")} live />
          <div className="cq-table-scroll">
            <table className="cq-list-table">
              <colgroup><col style={{ width: 72 }} /><col /><col style={{ width: 118 }} /></colgroup>
              <thead><tr><th>Token</th><th>Patient</th><th>Status</th></tr></thead>
              <tbody>
                {queue.length ? queue.map((item) => (
                  <tr key={item.id}>
                    <td><span className={`cq-token ${String(item.arrival_type || "").includes("WALK") ? "cq-token-walkin" : ""}`}>{item.token_no || "-"}</span></td>
                    <td><span className="cq-strong">{item.patient_name || "-"}</span><span className="cq-sub">with {item.doctor_name || "-"}</span></td>
                    <td><span className={`cq-status ${statusClass(item.status)}`}>{statusLabel(item.status)}</span></td>
                  </tr>
                )) : <tr><td colSpan="3"><div className="cq-empty">No patients are waiting.</div></td></tr>}
              </tbody>
            </table>
          </div>
        </article>

        <article className="cq-card cq-list-card">
          <CardHead icon={CalendarDays} title="Today's appointments" action="View all" onAction={() => navigate("/admin-dashboard/appointments")} />
          <div className="cq-table-scroll">
            <table className="cq-list-table">
              <colgroup><col style={{ width: 88 }} /><col /><col style={{ width: 118 }} /></colgroup>
              <thead><tr><th>Time</th><th>Patient</th><th>Status</th></tr></thead>
              <tbody>
                {todayAppointments.length ? todayAppointments.map((item) => (
                  <tr key={item.id}>
                    <td className="cq-strong">{formatTime(item.slot_time)}</td>
                    <td><span className="cq-strong">{item.patient_name || "-"}</span><span className="cq-sub">with {item.doctor_name || "-"}</span></td>
                    <td><span className={`cq-status ${statusClass(item.status)}`}>{statusLabel(item.status)}</span></td>
                  </tr>
                )) : <tr><td colSpan="3"><div className="cq-empty">No appointments scheduled for today.</div></td></tr>}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      {/* ================= DOCTORS (own full-width row) ================= */}
      <section className="cq-section">
        <article className="cq-card">
          <CardHead icon={UserRound} title="Doctors today" action="Manage" onAction={() => navigate("/admin-dashboard/doctors")} />
          <div className="cq-doctors">
            {doctors.length ? doctors.map((doctor) => {
              const active = activeDoctorIds.has(String(doctor.id));
              return (
                <div className="cq-doctor-card" key={doctor.id}>
                  <div className="cq-doctor-top">
                    <span className="cq-avatar">{initials(doctor.doctor_name)}</span>
                    <div><div className="cq-doctor-name">{doctor.doctor_name || "-"}</div><div className="cq-doctor-specialty">{doctor.specialization || "-"}</div></div>
                    <span className={`cq-badge ${active ? "" : "cq-badge-off"}`}>{active ? "Active" : "Inactive"}</span>
                  </div>
                  <div className="cq-doctor-foot"><Users size={13} />{appointmentCounts[doctor.id] || 0} patients today</div>
                </div>
              );
            }) : <div className="cq-empty">No doctors found for this clinic.</div>}
          </div>
        </article>
      </section>

      {/* ================= CHART + ACTIVITY ================= */}
      <section className="cq-section cq-grid cq-two">
        <article className="cq-card cq-chart-wrap">
          <div className="cq-chart-head">
            <div><div className="cq-chart-title">Patient flow</div><div className="cq-chart-sub">Appointments, walk-ins and completed visits this week</div></div>
            <button className="cq-range" type="button">Today<ChevronDown size={13} /></button>
          </div>
          <PatientFlowGraph rows={patientFlow} />
        </article>

        <article className="cq-card">
          <CardHead icon={Clock3} title="Recent activity" />
          {recentActivity.length ? recentActivity.map((item) => {
            const Icon = activityIcon(item.status);
            return (
              <div className="cq-activity" key={item.id}>
                <span className="cq-activity-icon"><Icon size={13} /></span>
                <span className="cq-activity-text">{item.patient_name || "Patient"} · {statusLabel(item.status)}</span>
                <span className="cq-activity-time">{formatTime(item.slot_time)}</span>
              </div>
            );
          }) : <div className="cq-empty">No activity recorded today.</div>}
        </article>
      </section>

      {/* ================= QUICK ACTIONS + PLAN/SUPPORT ================= */}
      <section className="cq-section cq-grid cq-two">
        <article className="cq-card">
          <div className="cq-head"><h2>Quick actions</h2></div>
          <div className="cq-actions">
            {quickActions.map(([label, Icon, path]) => (
              <button className="cq-quick" key={label} onClick={() => navigate(path)}>
                <span className="cq-quick-icon"><Icon size={17} /></span>
                <span className="cq-quick-label">{label}</span>
              </button>
            ))}
          </div>
        </article>

        <div className="cq-grid" style={{ gap: 18 }}>
          <article className="cq-plan">
            <span className="cq-plan-icon"><CreditCard size={17} /></span>
            <div><div className="cq-plan-title">{data.currentPlanName || "No active plan"}</div><div className="cq-plan-note">{data.endDate ? `Valid until ${formatDate(data.endDate)}` : "Subscription details"}</div></div>
            <button className="cq-link" style={{ marginLeft: "auto" }} onClick={() => navigate("/admin-dashboard/subscription")}>View plan<ArrowRight size={13} /></button>
          </article>
          <button className="cq-card cq-support" onClick={() => navigate("/admin-dashboard/settings")}>
            <span className="cq-support-icon"><Headphones size={16} /></span>
            <span><span className="cq-support-title">Need help?</span><span className="cq-support-note">Open clinic settings and support options.</span></span>
            <ArrowRight size={14} className="cq-support-arrow" />
          </button>
        </div>
      </section>

      <footer className="cq-foot">
        <span>{clinicName || "Clinic"}</span>
        <span>{lastSynced ? `Last synced ${lastSynced.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : "Syncing dashboard"}</span>
      </footer>
    </div>
  );
}